import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Button,
  Card,
  CardBody,
  Heading,
  Icon,
  Spinner,
} from '@chakra-ui/react';

import { MdSync } from 'react-icons/md';

import { useColors } from './colors';
import { getStoresListApi } from 'features/stores/stores.api';
import { synchronizeStoresApi } from 'features/store-synchronization/store-synchronization.api';

const SynchronizeStores = () => {
  const dispatch = useDispatch();

  const [selectedStores, setSelectedStores] = useState([]);

  // Redux state from stores
  const { list: storesList, getStoresListStatus } = useSelector((state) => state.stores);

  // Redux state from store synchronization
  const { synchronizeStoresStatus } = useSelector((state) => state.storeSynchronization);

  const {
    cardBgColor,
    borderColor,
    textColor,
    inputBgColor,
    backgroundCardColor,
    hoverBgColor,
    buttonPrimaryBg,
    buttonPrimaryHover,
  } = useColors();

  // Load stores on component mount
  useEffect(() => {
    dispatch(getStoresListApi());
  }, [dispatch]);

  const handleStoreSelection = (values) => {
    setSelectedStores(values);
  };

  const handleSync = async () => {
    if (selectedStores.length === 0) return;

    try {
      // Pass the array of store IDs directly to the API
      await dispatch(synchronizeStoresApi(selectedStores)).unwrap();
    } catch (error) {
      console.error('Sync failed:', error);
    }
  };

  const isLoading = synchronizeStoresStatus === 'pending';
  const isLoadingStores = getStoresListStatus === 'pending';

  return (
    <Card
      bg={cardBgColor}
      borderRadius="xl"
      shadow="sm"
      border="1px"
      borderColor={borderColor}
      w="50%"
    >
      <CardBody p={6}>
        <VStack spacing={6} align="stretch">
          <Heading size="lg" color={textColor}>
            Synchronize Stores
          </Heading>

          <Text color={textColor} fontSize="md">
            Select stores to synchronize right now.
          </Text>

          <FormControl>
            <FormLabel color={textColor} fontSize="md" fontWeight="medium">
              Select Stores
            </FormLabel>

            {isLoadingStores ? (
              <Box textAlign="center" py={4}>
                <Spinner color="blue.500" size="lg" />
                <Text mt={2} color={textColor}>
                  Loading stores...
                </Text>
              </Box>
            ) : (
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  bg={inputBgColor}
                  color={textColor}
                  borderColor={borderColor}
                  variant="outline"
                  w="100%"
                  size="lg"
                  borderRadius="lg"
                  _hover={{
                    bg: hoverBgColor,
                  }}
                  _active={{
                    bg: hoverBgColor,
                  }}
                  _focus={{
                    bg: inputBgColor,
                    borderColor: 'blue.300',
                  }}
                  textAlign="left"
                  justifyContent="flex-start"
                >
                  {selectedStores.length > 0
                    ? `${selectedStores.length} store${selectedStores.length > 1 ? 's' : ''} selected`
                    : 'Choose store(s) to sync'}
                </MenuButton>

                <MenuList
                  bg={backgroundCardColor}
                  borderColor={borderColor}
                  p={3}
                  minWidth="100%"
                  shadow="lg"
                >
                  <MenuOptionGroup
                    title="Stores"
                    type="checkbox"
                    value={selectedStores}
                    onChange={handleStoreSelection}
                    color={textColor}
                    fontSize="md"
                  >
                    {storesList.map((store) => (
                      <MenuItemOption
                        fontSize="lg"
                        key={store.id}
                        value={store.value}
                        bg="transparent"
                        color={textColor}
                        _hover={{
                          bg: hoverBgColor,
                        }}
                      >
                        {store.label}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>
            )}
          </FormControl>

          <HStack justify="flex-end">
            <Button
              bg={buttonPrimaryBg}
              color="white"
              size="lg"
              leftIcon={<Icon as={MdSync} />}
              onClick={handleSync}
              isLoading={isLoading}
              loadingText="Syncing..."
              isDisabled={selectedStores.length === 0 || isLoading || isLoadingStores}
              _hover={{
                bg: buttonPrimaryHover,
              }}
              _active={{
                bg: buttonPrimaryHover,
              }}
            >
              Sync Selected Stores
            </Button>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default SynchronizeStores;
